/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
/* =========================================================================
 * easybuy | Warenkorb: Versandfortschritt (Popup-Budget seit 16.10.2026 ersetzt)
 * -------------------------------------------------------------------------
 * Auftrag "Paket 1: Warenkorb, Upsell und Versandleiste", 14.10.2026.
 *
 * Es wird KEIN zweites Warenkorb-Bauteil gebaut. Focal 13.0.0 bringt den
 * Side Cart (<cart-drawer id="mini-cart">) samt Versandleiste und
 * Empfehlungen schon mit; laut Auftrag Abschnitt 4 sind erst die Bordmittel
 * zu pruefen. Umgesetzt ist daher die Steuerung, WANN sich der Side Cart von
 * selbst oeffnet:
 *
 *   1. beim ersten Hinzufuegen je Sitzung   -> oeffnet (Orientierung)
 *   2. bei rund 70 % der Versandschwelle    -> oeffnet mit Gratisversand-Hinweis
 *   sonst                                   -> bleibt zu, dezente Bestaetigung
 *
 * Nie zwei Hinzufuegungen hintereinander: nach einem Popup ist der direkt
 * folgende Zugang gesperrt.
 *
 * Angriffspunkt: Focal entscheidet das Oeffnen ueber detail.openMiniCart im
 * Ereignis "cart:refresh" (assets/theme.js). Dieser Hoerer laeuft in der
 * Capture-Phase und damit vor dem Side Cart; theme.js bleibt unangetastet.
 * ========================================================================= */
(function () {
  'use strict';

  var CFG = window.easybuyCart || {};
  var SESSION_KEY = 'eb:cart:v1';

  function readState() {
    try {
      return JSON.parse(window.sessionStorage.getItem(SESSION_KEY)) || {};
    } catch (error) {
      return {};
    }
  }

  function writeState(state) {
    try {
      window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
    } catch (error) {
      /* Privater Modus: Budget greift dann nur innerhalb des Seitenaufrufs. */
    }
  }

  /* Summe der versandpflichtigen Positionen, Warenkorbrabatt einmal abgezogen.
     Gleiche Rechnung wie snippets/easybuy-shipping-bar.liquid. */
  function shippingEligibleTotal(cart) {
    if (!cart || !cart.items) {
      return 0;
    }

    var total = 0;
    var index;

    for (index = 0; index < cart.items.length; index++) {
      if (cart.items[index].requires_shipping) {
        total += cart.items[index].final_line_price;
      }
    }

    var discounts = cart.cart_level_discount_applications || [];

    for (index = 0; index < discounts.length; index++) {
      total -= discounts[index].total_allocated_amount;
    }

    return total > 0 ? total : 0;
  }

  /* Die Oeffnungslogik des Side Carts aus Paket 1 (Popup-Budget) ist am
     16.10.2026 durch das Warenkorb-Fenster ersetzt (Block weiter unten). */

  /* Prozentzahl zwischen zwei Serverrenderings aktuell halten. */
  function updatePercent(cart) {
    var threshold = CFG.threshold || 0;

    if (!threshold) {
      return;
    }

    var total = shippingEligibleTotal(cart);
    var percent = Math.round((total / threshold) * 100);

    if (total < threshold && percent >= 100) {
      percent = 99;
    }

    var nodes = document.querySelectorAll('[data-eb-shipping-bar] [data-eb-role="percent"]');

    Array.prototype.forEach.call(nodes, function (node) {
      node.textContent = percent + ' %';
    });
  }

  /* Erreichen der Schwelle einmal je Sitzung quittieren. Die Animation selbst
     endet serverseitig: die neu gerenderte Leiste traegt dann
     eb-shipping-bar--reached statt eb-shipping-bar--animated.
     prefers-reduced-motion wird in der CSS-Regel beruecksichtigt. */
  function acknowledgeThreshold(cart) {
    var threshold = CFG.threshold || 0;

    if (!threshold) {
      return;
    }

    var state = readState();
    var total = shippingEligibleTotal(cart);

    if (total < threshold) {
      if (state.ackShown) {
        state.ackShown = false;
        writeState(state);
      }
      return;
    }

    if (state.ackShown) {
      return;
    }

    state.ackShown = true;
    writeState(state);

    /* Nach dem Neurendern des Side Carts greifen (Focal verzoegert es um
       bis zu 600 ms). */
    window.setTimeout(function () {
      var bars = document.querySelectorAll('[data-eb-shipping-bar]');

      Array.prototype.forEach.call(bars, function (bar) {
        bar.classList.add('eb-shipping-bar--ack');
        window.setTimeout(function () {
          bar.classList.remove('eb-shipping-bar--ack');
        }, 1000);
      });
    }, 750);
  }

  document.documentElement.addEventListener('cart:updated', function (event) {
    var cart = event.detail && event.detail.cart;
    updatePercent(cart);
    acknowledgeThreshold(cart);
  });
}());

/* =========================================================================
 * easybuy | Paket 2: Merkzettel
 * -------------------------------------------------------------------------
 * Gemerkte Artikel liegen als Handle-Liste im localStorage des Browsers.
 * Bewusst geraetegebunden: eine kontogebundene Merkliste braeuchte Kundendaten
 * und eine App. Der Drawer laedt die Artikeldaten erst beim Oeffnen ueber
 * /products/<handle>.js nach - so bleibt die Startseite unbelastet.
 * ========================================================================= */
(function () {
  'use strict';

  var KEY = 'eb:wishlist:v1';

  function read() {
    try {
      var raw = JSON.parse(window.localStorage.getItem(KEY));
      return Array.isArray(raw) ? raw : [];
    } catch (error) {
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list));
    } catch (error) {
      /* Privater Modus: Merkzettel haelt dann nur bis zum Neuladen. */
    }
  }

  function refreshCount() {
    var count = read().length;

    Array.prototype.forEach.call(document.querySelectorAll('[data-eb-wishlist-count]'), function (node) {
      node.textContent = count;
      node.hidden = count === 0;
    });
  }

  function refreshButtons() {
    var list = read();

    Array.prototype.forEach.call(document.querySelectorAll('[data-eb-wish]'), function (button) {
      var active = list.indexOf(button.getAttribute('data-eb-wish')) !== -1;
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', active ? 'Vom Merkzettel entfernen' : 'Auf den Merkzettel');
      button.setAttribute('title', button.getAttribute('aria-label'));
    });
  }

  function toggle(handle) {
    var list = read();
    var index = list.indexOf(handle);

    if (index === -1) {
      list.push(handle);
    } else {
      list.splice(index, 1);
    }

    write(list);
    refreshCount();
    refreshButtons();
  }

  /* Delegiert, damit auch nachgeladene Kacheln funktionieren
     (Empfehlungen, Quick View, Paginierung). */
  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest('[data-eb-wish]') : null;

    if (!button) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    toggle(button.getAttribute('data-eb-wish'));
  });

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  async function renderDrawer() {
    var target = document.querySelector('[data-eb-wishlist-content]');

    if (!target) {
      return;
    }

    var handles = read();

    if (handles.length === 0) {
      /* 16.10.2026: Text aus locales/*.json (data-eb-empty-text), da das Herz jetzt in allen Sprachen im Kopf steht */
      var emptyP = document.createElement('p');
      emptyP.className = 'eb-wishlist__empty text--subdued';
      emptyP.textContent = target.getAttribute('data-eb-empty-text') || 'Noch nichts gemerkt.';
      target.innerHTML = '';
      target.appendChild(emptyP);
      return;
    }

    target.innerHTML = '<div class="spinner"></div>';

    var products = await Promise.all(handles.map(function (handle) {
      return fetch(window.Shopify.routes.root + 'products/' + handle + '.js')
        .then(function (response) { return response.ok ? response.json() : null; })
        .catch(function () { return null; });
    }));

    var rows = products.filter(Boolean).map(function (product) {
      var image = product.featured_image ? product.featured_image : '';
      var available = product.available;

      return '' +
        '<div class="eb-wishlist__item line-item">' +
          '<a class="eb-wishlist__image" href="' + escapeHtml(product.url) + '" tabindex="-1" aria-hidden="true">' +
            (image ? '<img src="' + escapeHtml(image) + '&width=160" alt="" width="64" height="64" loading="lazy">' : '') +
          '</a>' +
          '<div class="eb-wishlist__info">' +
            '<a class="product-item-meta__title text--small" href="' + escapeHtml(product.url) + '">' + escapeHtml(product.title) + '</a>' +
            '<span class="price text--small">' + (available ? '' : '<span class="text--subdued">Zurzeit nicht verfügbar</span> ') + '</span>' +
            '<button type="button" class="link text--subdued text--xxsmall" data-eb-wish="' + escapeHtml(product.handle) + '">Entfernen</button>' +
          '</div>' +
        '</div>';
    });

    target.innerHTML = rows.length
      ? '<div class="eb-wishlist__list">' + rows.join('') + '</div>'
      : '<p class="eb-wishlist__empty text--subdued">Die gemerkten Artikel sind nicht mehr verfügbar.</p>';
  }

  var drawer = null;

  function watchDrawer() {
    drawer = document.getElementById('eb-wishlist-drawer');

    if (!drawer) {
      return;
    }

    new MutationObserver(function () {
      if (drawer.hasAttribute('open')) {
        renderDrawer();
      }
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  function init() {
    refreshCount();
    refreshButtons();
    watchDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Warenkorbzaehler der Fussleiste mitfuehren. */
  document.documentElement.addEventListener('cart:updated', function (event) {
    var count = event.detail && event.detail.cart ? event.detail.cart.item_count : 0;

    Array.prototype.forEach.call(document.querySelectorAll('[data-eb-cart-count]'), function (node) {
      node.textContent = count;
      node.hidden = count === 0;
    });
  });
}());

/* =========================================================================
 * easybuy | Warenkorb-Fenster nach dem Hinzufuegen (16.10.2026)
 * -------------------------------------------------------------------------
 * Vorgabe Marketingleitung: Fenster wie bei amazingtoys ueber die ganze
 * Seite, danach Side Cart. Markup und Gestaltung: snippets/easybuy-cart-popup.
 *
 * Ablauf: Focal meldet "variant:added" (der hinzugefuegte Artikel) und danach
 * "cart:refresh" mit detail.openMiniCart. Dieser Hoerer laeuft in der
 * Capture-Phase vor dem Side Cart, setzt openMiniCart auf false und oeffnet
 * stattdessen das Fenster. Der Side Cart wird trotzdem neu gezeichnet und
 * steht bei "Zum Warenkorb" aktuell bereit. theme.js bleibt unangetastet.
 *
 * Haeufigkeit (Einstellung cart_popup_mode): "every" = bei jedem
 * Hinzufuegen, "first" = beim ersten je Sitzung und beim 7. Artikel, sonst
 * nur Animation am Warenkorb-Symbol (seit 22.10.2026, vorher oeffnete sich
 * danach jedes Mal der Side Cart). Seit 16.10.2026 zaehlt "erstes" neu,
 * sobald der Warenkorb leer ist (sonst blieb das Fenster nach einem Test im
 * selben Tab dauerhaft weg).
 *
 * Ladezeit (16.10.2026): Kopf und Empfehlungen laden parallel; auf
 * Produktseiten werden die Empfehlungen des Artikels vorab geholt.
 *
 * Vorschlaege (16.10.2026, Vorgabe Marketingleitung): zuerst das Upsell-
 * Regelwerk wie im Side Cart (sections/easybuy-cart-popup-rules.liquid,
 * Ueberschrift "Passendes Zubehoer" bzw. "Passende Ergaenzungen"). Nur fuer
 * Artikel ohne Regel-Welt gelten wie bisher die Shopify-Empfehlungen.
 * ========================================================================= */
(function () {
  'use strict';

  var CFG = window.easybuyCartPopup;
  var root = document.getElementById('eb-cart-popup');

  if (!CFG || !CFG.enabled || !root) {
    return;
  }

  var panel = root.querySelector('.eb-pop__panel');
  var slots = {
    head: root.querySelector('[data-eb-slot="head"]'),
    bar: root.querySelector('[data-eb-slot="bar"]'),
    complementary: root.querySelector('[data-eb-slot="complementary"]'),
    related: root.querySelector('[data-eb-slot="related"]'),
    loading: root.querySelector('[data-eb-slot="loading"]')
  };
  var groups = {
    complementary: root.querySelector('[data-eb-group="complementary"]'),
    related: root.querySelector('[data-eb-group="related"]')
  };
  var complementaryHeading = groups.complementary.querySelector('.eb-pop__h');
  var complementaryHeadingText = complementaryHeading ? complementaryHeading.textContent : '';
  var routes = (window.themeVariables && window.themeVariables.routes) || {};
  var SEEN_KEY = 'eb:cartpopup:seen';
  var pending = null;
  var current = null;
  var lastFocus = null;
  var requestId = 0;
  var hideTimer = null;

  function seen() {
    try {
      return window.sessionStorage.getItem(SEEN_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function markSeen() {
    try {
      window.sessionStorage.setItem(SEEN_KEY, '1');
    } catch (error) {
      /* Privater Modus: dann gilt "first" nur innerhalb des Seitenaufrufs. */
    }
  }

  function resetSeen() {
    try {
      window.sessionStorage.removeItem(SEEN_KEY);
    } catch (error) {
      /* siehe markSeen */
    }
  }

  /* Leerer Warenkorb = das naechste Hinzufuegen ist wieder das erste. */
  if (CFG.cartEmpty) {
    resetSeen();
  }

  document.documentElement.addEventListener('cart:updated', function (event) {
    if (event.detail && event.detail.cart && event.detail.cart.item_count === 0) {
      resetSeen();
    }
  });

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  /* Sprachpfad (/en, /fr) beibehalten, damit die Inhalte in der Sprache der Seite kommen. */
  function localePath(path) {
    return (routes.rootUrlWithoutSlash || '') + path;
  }

  function fetchDocument(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (response) {
      if (!response.ok) {
        throw new Error('HTTP ' + response.status);
      }
      return response.text();
    }).then(function (text) {
      return new DOMParser().parseFromString(text, 'text/html');
    });
  }

  function headUrl(variant) {
    return localePath('/products/' + encodeURIComponent(variant.handle)) +
      '?variant=' + variant.variant_id + '&section_id=easybuy-cart-popup-head';
  }

  /* Sofortanzeige aus der Antwort von /cart/add, bis der Server-Kopf da ist. */
  function quickHead(variant) {
    var image = (variant.featured_image && variant.featured_image.url) || variant.image || '';

    return '<div class="eb-pop-head"><div class="eb-pop-head__media">' +
      (image ? '<img src="' + escapeHtml(image) + '" alt="" width="96" height="96">' : '') +
      '</div><div class="eb-pop-head__info"><p class="eb-pop-head__title">' + escapeHtml(variant.product_title) + '</p>' +
      (variant.variant_title ? '<p class="eb-pop-head__variant">' + escapeHtml(variant.variant_title) + '</p>' : '') +
      '<p class="eb-pop-head__status"><span>' + escapeHtml(CFG.strings.added) + '</span></p></div></div>';
  }

  function loadHead(variant, id) {
    return fetchDocument(headUrl(variant)).then(function (doc) {
      if (id !== requestId) {
        return null;
      }

      var head = doc.querySelector('.eb-pop-head');
      var bar = doc.querySelector('.eb-pop-bar');

      if (head) {
        slots.head.innerHTML = '';
        slots.head.appendChild(document.adoptNode(head));
      }

      slots.bar.innerHTML = '';

      if (bar && bar.querySelector('[data-eb-shipping-bar]')) {
        slots.bar.appendChild(document.adoptNode(bar));
      }

      return head ? (head.getAttribute('data-eb-series') || '') : '';
    }).catch(function () {
      return '';
    });
  }

  /* Vorab geholte Empfehlungen je Produkt und Art (nur Text, kein DOM). */
  var recsCache = {};

  function recsUrl(productId, intent) {
    var base = routes.productRecommendationsUrl || localePath('/recommendations/products');
    return base + '?product_id=' + productId + '&limit=10&intent=' + intent +
      '&section_id=easybuy-cart-popup-recs';
  }

  function fetchRecsText(productId, intent) {
    var key = productId + '|' + intent;

    if (!recsCache[key]) {
      recsCache[key] = fetch(recsUrl(productId, intent), { credentials: 'same-origin' }).then(function (response) {
        if (!response.ok) {
          throw new Error('HTTP ' + response.status);
        }
        return response.text();
      }).catch(function (error) {
        delete recsCache[key];
        throw error;
      });
    }

    return recsCache[key];
  }

  /* Regelwerk-Vorschlaege je Produkt (Text-Cache, wie die Empfehlungen). */
  var rulesCache = {};

  function fetchRulesText(handle) {
    if (!rulesCache[handle]) {
      rulesCache[handle] = fetch(localePath('/products/' + encodeURIComponent(handle)) + '?section_id=easybuy-cart-popup-rules', { credentials: 'same-origin' }).then(function (response) {
        if (!response.ok) {
          throw new Error('HTTP ' + response.status);
        }
        return response.text();
      }).catch(function (error) {
        delete rulesCache[handle];
        throw error;
      });
    }

    return rulesCache[handle];
  }

  function loadRules(variant, id) {
    return fetchRulesText(variant.handle).then(function (text) {
      if (id !== requestId) {
        return null;
      }

      var doc = new DOMParser().parseFromString(text, 'text/html');
      var box = doc.querySelector('.eb-pop-rules');
      var cards = box ? Array.prototype.slice.call(box.querySelectorAll('.eb-pop-card')) : [];

      return cards.length ? { heading: box.getAttribute('data-eb-heading') || '', cards: cards } : null;
    }).catch(function () {
      return null;
    });
  }

  function loadRecommendations(variant, intent, id) {
    return fetchRecsText(variant.product_id, intent).then(function (text) {
      return new DOMParser().parseFromString(text, 'text/html');
    }).then(function (doc) {
      if (id !== requestId) {
        return [];
      }
      return Array.prototype.slice.call(doc.querySelectorAll('.eb-pop-card'));
    }).catch(function () {
      return [];
    });
  }

  /* Karten einsetzen: keine Doppelten, nichts aus dem Warenkorb, bei bekannter
     Reihe nur dieselbe Reihe. */
  function fill(name, cards, shown, series, max) {
    var slot = slots[name];
    var used = 0;

    slot.innerHTML = '';

    cards.forEach(function (card) {
      if (used >= max) {
        return;
      }

      var productId = card.getAttribute('data-eb-product-id');

      if (shown[productId]) {
        return;
      }

      if (series && card.getAttribute('data-eb-series') !== series) {
        return;
      }

      shown[productId] = true;
      slot.appendChild(document.adoptNode(card));
      used++;
    });

    groups[name].hidden = used === 0;
    return used;
  }

  function open(variant, cart) {
    var id = ++requestId;
    var shown = {};

    current = variant;

    ((cart && cart.items) || []).forEach(function (item) {
      shown[String(item.product_id)] = true;
    });
    shown[String(variant.product_id)] = true;

    window.clearTimeout(hideTimer);
    slots.head.innerHTML = quickHead(variant);
    slots.bar.innerHTML = '';
    slots.complementary.innerHTML = '';
    slots.related.innerHTML = '';
    groups.complementary.hidden = true;
    groups.related.hidden = true;
    slots.loading.hidden = false;

    if (root.hidden) {
      lastFocus = document.activeElement;
    }

    root.hidden = false;
    document.documentElement.classList.add('eb-pop-lock');

    window.requestAnimationFrame(function () {
      root.classList.add('is-open');
      panel.focus({ preventScroll: true });
    });

    if (complementaryHeading) {
      complementaryHeading.textContent = complementaryHeadingText;
    }

    Promise.all([
      loadHead(variant, id),
      loadRules(variant, id)
    ]).then(function (first) {
      if (id !== requestId) {
        return;
      }

      var rules = first[1];

      /* Regelwerk greift: eine Gruppe mit eigener Ueberschrift. */
      if (rules) {
        if (complementaryHeading && rules.heading) {
          complementaryHeading.textContent = rules.heading;
        }
        fill('complementary', rules.cards, shown, null, 10);
        groups.related.hidden = true;
        slots.loading.hidden = true;
        return;
      }

      /* Sonst wie bisher: Shopify-Empfehlungen. */
      return Promise.all([
        loadRecommendations(variant, 'complementary', id),
        loadRecommendations(variant, 'related', id)
      ]).then(function (results) {
        if (id !== requestId) {
          return;
        }

        fill('complementary', results[0], shown, null, 5);
        fill('related', results[1], shown, first[0] || null, 10);
        slots.loading.hidden = true;
      });
    });
  }

  function close(openCart) {
    requestId++;
    root.classList.remove('is-open');
    document.documentElement.classList.remove('eb-pop-lock');

    window.clearTimeout(hideTimer);
    hideTimer = window.setTimeout(function () {
      root.hidden = true;
    }, 250);

    if (openCart) {
      var drawer = document.getElementById('mini-cart');

      if (drawer) {
        drawer.open = true;
      } else {
        window.location.href = routes.cartUrl || '/cart';
      }
      return;
    }

    if (lastFocus && typeof lastFocus.focus === 'function') {
      lastFocus.focus();
    }
  }

  /* Nach einem Zukauf im Fenster nur die Versandleiste neu holen. */
  function refreshBar() {
    if (!current) {
      return;
    }

    var id = requestId;

    fetchDocument(headUrl(current)).then(function (doc) {
      if (id !== requestId) {
        return;
      }

      var bar = doc.querySelector('.eb-pop-bar');
      slots.bar.innerHTML = '';

      if (bar && bar.querySelector('[data-eb-shipping-bar]')) {
        slots.bar.appendChild(document.adoptNode(bar));
      }
    }).catch(function () {});
  }

  /* Ladezeit (16.10.2026): Haken sofort zeigen, eine Anfrage statt zwei
     (update.js liefert Warenkorb und neu gezeichneten Side Cart). Das Fenster
     zeigt nur Artikel, die noch nicht im Warenkorb liegen; Menge 1 ist daher
     korrekt. Bei Fehler wird der Knopf zurueckgesetzt. */
  function addFromPopup(button) {
    var variantId = button.getAttribute('data-eb-add');
    var label = button.textContent;
    var updates = {};
    updates[variantId] = 1;

    button.disabled = true;
    button.classList.add('is-added');
    button.textContent = '✓ ' + CFG.strings.addedShort;

    fetch((routes.cartUrl || '/cart') + '/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates: updates, sections: ['mini-cart'] })
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('add');
      }
      return response.json();
    }).then(function (cart) {
      /* Zaehler im Kopf und Side Cart aktualisieren, ohne ihn zu oeffnen. */
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart, openMiniCart: false } }));
      refreshBar();
    }).catch(function () {
      button.disabled = false;
      button.classList.remove('is-added');
      button.textContent = CFG.strings.errorAdd || label;
    });
  }

  root.addEventListener('click', function (event) {
    var target = event.target;

    if (target.closest('[data-eb-open-cart]')) {
      close(true);
      return;
    }

    if (target.closest('[data-eb-close]')) {
      close(false);
      return;
    }

    var add = target.closest('[data-eb-add]');

    if (add && !add.disabled && !add.classList.contains('is-added')) {
      addFromPopup(add);
    }
  });

  root.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(false);
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    var focusable = Array.prototype.filter.call(
      panel.querySelectorAll('a[href], button:not([disabled])'),
      function (element) { return element.offsetParent !== null; }
    );

    if (!focusable.length) {
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* Auf Produktseiten die Empfehlungen im Leerlauf vorab holen. */
  if (CFG.productId) {
    var warm = function () {
      if (CFG.productHandle) {
        fetchRulesText(CFG.productHandle).then(function (text) {
          /* Ohne Regel-Welt die bisherigen Empfehlungen vorab holen. */
          if (text.indexOf('data-eb-world=""') !== -1) {
            fetchRecsText(CFG.productId, 'related').catch(function () {});
            fetchRecsText(CFG.productId, 'complementary').catch(function () {});
          }
        }).catch(function () {});
      } else {
        fetchRecsText(CFG.productId, 'related').catch(function () {});
        fetchRecsText(CFG.productId, 'complementary').catch(function () {});
      }
    };
    window.addEventListener('load', function () {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(warm, { timeout: 3000 });
      } else {
        window.setTimeout(warm, 1500);
      }
    });
  }

  /* Ladezeit (16.10.2026): Das Fenster oeffnet schon bei "variant:added"
     (Antwort von /cart/add), nicht erst nach dem zweiten Abruf von /cart.js.
     Artikel, die bereits im Warenkorb liegen, werden nachtraeglich
     ausgeblendet, sobald der Warenkorb vorliegt. */
  var openedEarly = false;
  var cartProductIds = null;

  function hideCartItems() {
    if (!cartProductIds) {
      return;
    }

    Array.prototype.forEach.call(root.querySelectorAll('.eb-pop-card[data-eb-product-id]'), function (card) {
      if (cartProductIds[card.getAttribute('data-eb-product-id')] && !card.querySelector('.is-added')) {
        card.hidden = true;
      }
    });
  }

  if (window.MutationObserver) {
    new MutationObserver(function () {
      if (cartProductIds) {
        hideCartItems();
      }
    }).observe(root, { childList: true, subtree: true });
  }

  /* Haeufigkeit (22.10.2026, Vorgabe Marketingleitung): Im Modus "first"
     erscheint das Fenster beim ersten Hinzufuegen je Sitzung und noch einmal,
     wenn der Warenkorb CFG.repeatAt Artikel (Stueckzahl, wie der Zaehler im
     Kopf) erreicht. Dazwischen und danach oeffnet sich weder Fenster noch
     Side Cart: ein Punkt fliegt vom Knopf zum Warenkorb-Symbol, das Symbol
     wippt und der Zaehler steigt. Ist das Symbol nicht im Bild (der Kopf
     scrollt mit), erscheint kurz ein schwebendes Warenkorb-Symbol oben rechts. */
  var REPEAT_KEY = 'eb:cartpopup:repeat';
  var repeatAt = CFG.repeatAt || 7;
  var cartCount = CFG.cartCount || 0;
  var quiet = false;
  var lastClick = null;
  var fab = document.getElementById('eb-cart-fab');
  var fabCount = fab ? fab.querySelector('[data-eb-fab-count]') : null;
  var live = document.getElementById('eb-cart-live');
  var fabTimer = null;

  function repeatSeen() {
    try {
      return window.sessionStorage.getItem(REPEAT_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function markRepeat() {
    try {
      window.sessionStorage.setItem(REPEAT_KEY, '1');
    } catch (error) {
      /* siehe markSeen */
    }
  }

  if (CFG.cartEmpty) {
    try {
      window.sessionStorage.removeItem(REPEAT_KEY);
    } catch (error) {
      /* siehe markSeen */
    }
  }

  function setCount(count) {
    cartCount = count;
    if (fabCount) {
      fabCount.textContent = count;
    }
  }

  document.documentElement.addEventListener('cart:updated', function (event) {
    if (event.detail && event.detail.cart) {
      setCount(event.detail.cart.item_count);
      if (event.detail.cart.item_count === 0) {
        try {
          window.sessionStorage.removeItem(REPEAT_KEY);
        } catch (error) {
          /* siehe markSeen */
        }
      }
    }
  });

  /* Ausloeser merken: der feste Kaufknopf mobil steht ausserhalb des Formulars. */
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('button[type="submit"], button:not([type])');
    if (button) {
      lastClick = { node: button, time: Date.now() };
    }
  }, true);

  function inView(node) {
    if (!node) {
      return false;
    }
    var rect = node.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
  }

  function headerIcon() {
    var icons = document.querySelectorAll('.header__icon-wrapper[aria-controls="mini-cart"], .header__icon-wrapper[href$="/cart"]');
    for (var index = 0; index < icons.length; index++) {
      if (inView(icons[index])) {
        return icons[index];
      }
    }
    return null;
  }

  function sourceButton(form) {
    if (lastClick && Date.now() - lastClick.time < 10000 && inView(lastClick.node)) {
      return lastClick.node;
    }
    var button = form && form.querySelector ? form.querySelector('[type="submit"]') : null;
    return inView(button) ? button : null;
  }

  function addedQuantity(form) {
    try {
      var value = parseInt(new FormData(form).get('quantity'), 10);
      return value > 0 ? value : 1;
    } catch (error) {
      return 1;
    }
  }

  function bump(node) {
    node.classList.remove('eb-cart-bump');
    void node.offsetWidth;
    node.classList.add('eb-cart-bump');
  }

  function hideFab() {
    window.clearTimeout(fabTimer);
    fab.classList.remove('is-visible');
    fab.hidden = true;
  }

  /* Klick oeffnet den Side Cart (toggle-link); das Symbol geht dabei weg. */
  if (fab) {
    fab.addEventListener('click', hideFab);
  }

  function showFab() {
    if (!fab) {
      return null;
    }
    window.clearTimeout(fabTimer);
    fab.hidden = false;
    void fab.offsetWidth;
    fab.classList.add('is-visible');
    fabTimer = window.setTimeout(function () {
      fab.classList.remove('is-visible');
      fabTimer = window.setTimeout(function () {
        fab.hidden = true;
      }, 300);
    }, 2800);
    return fab;
  }

  function celebrate(form) {
    var target = headerIcon() || showFab();
    var source = sourceButton(form);
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (live) {
      live.textContent = '';
      window.setTimeout(function () { live.textContent = CFG.strings.added; }, 50);
    }

    if (!target) {
      return;
    }

    if (reduced || !source || !document.body.animate) {
      bump(target);
      return;
    }

    var from = source.getBoundingClientRect();
    var to = target.getBoundingClientRect();
    var dot = document.createElement('span');
    var startX = from.left + from.width / 2;
    var startY = from.top + from.height / 2;
    var dx = to.left + to.width / 2 - startX;
    var dy = to.top + to.height / 2 - startY;

    dot.className = 'eb-cart-fly';
    dot.setAttribute('aria-hidden', 'true');
    dot.style.left = startX + 'px';
    dot.style.top = startY + 'px';
    document.body.appendChild(dot);

    dot.animate([
      { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
      { transform: 'translate(calc(-50% + ' + dx * 0.5 + 'px), calc(-50% + ' + (dy * 0.5 - 60) + 'px)) scale(.9)', opacity: 1, offset: 0.5 },
      { transform: 'translate(calc(-50% + ' + dx + 'px), calc(-50% + ' + dy + 'px)) scale(.45)', opacity: .9 }
    ], { duration: 600, easing: 'cubic-bezier(.45, 0, .25, 1)' }).onfinish = function () {
      dot.remove();
      bump(target);
    };
  }

  document.addEventListener('variant:added', function (event) {
    var target = event.target;

    /* Zukaeufe aus Side Cart oder Fenster oeffnen kein Fenster. */
    if (target && target.closest && target.closest('#mini-cart, #eb-cart-popup')) {
      pending = null;
      return;
    }

    var before = cartCount;
    var after = before + addedQuantity(target);
    var reachesRepeat = before < repeatAt && after >= repeatAt;
    var shouldShow = CFG.mode !== 'first' || !seen() || (reachesRepeat && !repeatSeen());

    setCount(after);
    pending = shouldShow ? (event.detail && event.detail.variant) || null : null;
    cartProductIds = null;
    quiet = false;

    if (pending) {
      if (after >= repeatAt) {
        markRepeat();
      }
      markSeen();
      openedEarly = true;
      cartProductIds = null;
      open(pending, null);
      return;
    }

    /* Ohne Fenster: Side Cart bleibt zu (Hoerer in easybuy-cart-assets). */
    quiet = true;
    event.ebQuiet = true;
    celebrate(target);
  }, true);

  window.addEventListener('cart:refresh', function (event) {
    var detail = event.detail;

    if (detail && quiet) {
      quiet = false;
      detail.openMiniCart = false;
      return;
    }

    if (!detail || !detail.openMiniCart || !pending) {
      return;
    }

    if (openedEarly) {
      pending = null;
      openedEarly = false;
      detail.openMiniCart = false;
      cartProductIds = {};
      ((detail.cart && detail.cart.items) || []).forEach(function (item) {
        cartProductIds[String(item.product_id)] = true;
      });
      hideCartItems();
      return;
    }

    var variant = pending;
    pending = null;

    detail.openMiniCart = false;
    markSeen();
    open(variant, detail.cart);
  }, true);
}());
